import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary-strong disabled:bg-grayscale-line",
        destructive:
          "bg-danger text-danger-foreground hover:bg-danger-strong disabled:bg-grayscale-line",
        "destructive-outline":
          "bg-transparent border border-danger !text-danger-strong hover:text-danger-strong",
        outline: "border border-primary !text-primary bg-primary-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary-strong",
        ghost:
          "text-primary hover:bg-primary-foreground/80 hover:text-primary-strong disabled:text-primary-weak",
        link: "!h-auto !p-0 text-primary hover:bg-primary-foreground/80 hover:text-primary-strong disabled:!text-primary-weak underline-offset-4 hover:underline",
      },
      size: {
        small:
          "h-9 px-4 py-2 text-desktop-b5 font-semibold rounded-lg has-[>svg]:gap-x-3",
        default:
          "h-10.5 px-4 py-2.5 text-desktop-b3 font-semibold rounded-lg has-[>svg]:gap-x-3",
        large:
          "h-16 px-6 py-5 text-desktop-b4 font-semibold rounded-xl has-[>svg]:gap-x-4.5",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  block,
  children,
  isLoading = null,
  asChild = false,
  ...props
}) {
  const Comp = asChild ? Slot : "button";

  if (isLoading !== null) {
    return (
      <Comp
        data-slot="button"
        className={cn(
          buttonVariants({ variant, size, className }),
          block && "w-full"
        )}
        {...props}
      >
        <LoaderCircle
          className={cn("hidden w-0", {
            "inline-block animate-spin w-auto transition-all duration-300":
              isLoading,
          })}
        />

        {children}
      </Comp>
    );
  }

  return (
    <Comp
      data-slot="button"
      className={cn(
        buttonVariants({ variant, size, className }),
        block && "w-full"
      )}
      {...props}
    >
      {children}
    </Comp>
  );
}

export { Button, buttonVariants };
